// Reusable "focus flash" for notification / deep-link navigation.
//
// A notification link carries a `focus<Thing>=<id>` URL param (e.g.
// `?focusTask=`, `?focusComment=`, `?focusAttachment=`). After the target page
// mounts, the relevant view scrolls the item into view and briefly BLINKS it so
// the user can locate it. Every surface reuses these two helpers + the shared
// `.focus-flash` CSS class (index.css) so new notification types adopt the same
// behaviour by simply (a) emitting a `focus…` param and (b) tagging the target
// element with `data-focus-id="<id>"`.

/** Duration the highlight stays visible, in ms. Mirrors the `.focus-flash`
 *  CSS animation length — keep the two in sync. */
export const FOCUS_FLASH_MS = 4000

/**
 * Blink/flash-highlight an element for ~FOCUS_FLASH_MS, then leave it exactly
 * as it was (no lingering classes / styles). Uses the Web Animations API
 * (`el.animate`) rather than a CSS class: a class would be wiped the moment
 * React re-renders the element (its `className` is React-controlled), whereas
 * a WAAPI animation runs independently of React and cleans itself up. The
 * accent colours are read from the element's computed CSS vars, so it tracks
 * light/dark mode automatically. Honours `prefers-reduced-motion` with a
 * steady (non-pulsing) highlight. Returns a cleanup that cancels early.
 */
export function flashElement(el) {
  if (!el || typeof el.animate !== 'function') return () => {}
  const cs = getComputedStyle(el)
  const accent = cs.getPropertyValue('--color-accent').trim() || '#04BADE'
  const accentBg =
    cs.getPropertyValue('--color-accent-bg').trim() || 'rgba(4, 186, 222, 0.14)'
  // INSET ring (drawn INSIDE the element) so it's symmetric on all four sides
  // and never bleeds into the row above/below — an outer ring's top edge
  // overlapped the previous row and read as a thick, uneven top border.
  const off = 'inset 0 0 0 0 rgba(4, 186, 222, 0)'
  const on = `inset 0 0 0 2px ${accent}`
  // The person's Reduce movement preference, as resolved by display-prefs.js
  // onto <html data-motion> ("Computer" already folds in the OS setting);
  // falls back to the OS setting before that attribute exists.
  const motionAttr = document.documentElement.dataset.motion
  const reduce =
    motionAttr === 'less' ||
    (motionAttr !== 'normal' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches)

  const keyframes = reduce
    ? [
        { boxShadow: on, backgroundColor: accentBg },
        { boxShadow: on, backgroundColor: accentBg },
      ]
    : [
        { boxShadow: off, backgroundColor: 'transparent', offset: 0 },
        { boxShadow: on, backgroundColor: accentBg, offset: 0.15 },
        { boxShadow: off, backgroundColor: accentBg, offset: 0.4 },
        { boxShadow: on, backgroundColor: accentBg, offset: 0.55 },
        { boxShadow: off, backgroundColor: accentBg, offset: 0.8 },
        { boxShadow: off, backgroundColor: 'transparent', offset: 1 },
      ]

  let anim
  try {
    anim = el.animate(keyframes, {
      duration: FOCUS_FLASH_MS,
      easing: 'ease-in-out',
      // No `fill` — the element returns to its natural styles when the
      // animation ends, so nothing lingers past ~4s.
    })
  } catch {
    return () => {}
  }
  return () => {
    try {
      anim.cancel()
    } catch {
      /* already finished */
    }
  }
}

/**
 * Poll the DOM for `[data-focus-id="<id>"]` (rows/comments/attachments render
 * asynchronously on SPA navigation), then scroll it into view + flash it. Fires
 * the work ONCE. Returns a cleanup that cancels any pending poll/animation.
 *
 * @param {string} id            the focus id to find
 * @param {object} [opts]
 * @param {HTMLElement|null} [opts.root]   container to scope the query (defaults to document)
 * @param {number} [opts.attempts]         max poll attempts (default 40 ≈ 5s)
 * @param {number} [opts.intervalMs]       poll interval (default 120)
 * @param {() => void} [opts.onFound]      run when the element is found (before scroll)
 */
export function scrollAndFlash(id, opts = {}) {
  const { root = null, attempts = 40, intervalMs = 120, onFound } = opts
  if (!id) return () => {}
  let cancelled = false
  const timers = []
  let cleanupFlash = () => {}
  let tries = 0
  // A QUOTED attribute value needs no CSS.escape (that's for identifiers and
  // would mangle ids starting with a digit, e.g. a UUID like "4fa6…" →
  // "\34 fa6…", which then matches nothing). UUIDs never contain a `"`, so the
  // raw value is safe — and this stays consistent with the list-view lookup.
  const sel = `[data-focus-id="${String(id).replace(/"/g, '\\"')}"]`
  const find = () => (root ?? document).querySelector(sel)
  const tryOnce = () => {
    if (cancelled) return
    const el = find()
    if (el) {
      onFound?.()
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      cleanupFlash = flashElement(el)
      // Re-scroll once after layout settles (late-rendering rows above can
      // shift the position) — matches the existing focusTask behaviour.
      timers.push(
        setTimeout(() => {
          if (!cancelled) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 350),
      )
      return
    }
    if (tries++ < attempts) timers.push(setTimeout(tryOnce, intervalMs))
  }
  timers.push(setTimeout(tryOnce, 100))
  return () => {
    cancelled = true
    timers.forEach(clearTimeout)
    cleanupFlash()
  }
}
